import { Alert, TextField } from "@mui/material";

import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { useUpdateSession } from "@/shared/domain/sessions/hooks";
import { useEditableField } from "@/shared/hooks";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./EditableSessionDescription.module.css";

interface EditableSessionDescriptionProps {
  description: string | null;
  sessionId: string;
}

export function EditableSessionDescription({
  description,
  sessionId,
}: EditableSessionDescriptionProps) {
  const { mutateAsync: updateSession, error } = useUpdateSession();

  const { editable, currentValue, beginEdit, cancel, save } = useEditableField({
    initial: description ?? "",
    onSave: (trimmed) =>
      updateSession({
        sessionId,
        // An emptied field is sent as `null`, which clears the description
        // server-side; an empty string would be stored as the description.
        updates: { description: trimmed || null },
      }),
  });

  return (
    <div className={styles.container}>
      <DescriptionEditor
        editable={editable}
        currentDescription={currentValue}
        onCancel={cancel}
        onBeginEdit={beginEdit}
        onSave={save}
      />
      <MutationErrorAlert error={error} />
    </div>
  );
}

interface DescriptionEditorProps {
  editable: boolean;
  currentDescription: string;
  onBeginEdit: () => void;
  onCancel: () => void;
  onSave: (value: string) => Promise<void>;
}

function DescriptionEditor({
  editable,
  currentDescription,
  onBeginEdit,
  onCancel,
  onSave,
}: DescriptionEditorProps) {
  if (editable) {
    return (
      <TextField
        autoFocus
        size="small"
        fullWidth
        multiline
        minRows={2}
        maxRows={10}
        defaultValue={currentDescription}
        placeholder="No description"
        onBlur={(e) => void onSave(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onCancel();
            return;
          }
          // Shift+Enter saves; plain Enter inserts a newline.
          if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault();
            void onSave((e.target as HTMLTextAreaElement).value);
          }
        }}
        className={styles.textField}
        inputProps={{
          style: {
            fontSize: "var(--font-size-sm)",
            color: "var(--text-secondary)",
          },
        }}
      />
    );
  }

  return (
    <div
      className={styles.description}
      onClick={onBeginEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onBeginEdit();
        }
      }}
    >
      {currentDescription ? (
        <RobotoMarkdown content={currentDescription} />
      ) : (
        <i>No description</i>
      )}
    </div>
  );
}

function MutationErrorAlert({ error }: { error: Error | null }) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.inlineError}>
      {userFacingErrorMessage(error, "Failed to update session description.")}
    </Alert>
  );
}
